import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale/fr';
import { format as formatDate } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Table from '../components/Listing/Table';
import Modal from '../components/Modal/Modal';
import {
    faPlus, faTrash, faSave, faCheckCircle,
    faInfoCircle, faBoxesStacked, faTriangleExclamation,
    faDownload, faCalendarDays, faUser, faLayerGroup,
    faHandPointer, faArrowsRotate, faHourglassHalf, faFileLines,
    faEllipsisH,
} from '@fortawesome/free-solid-svg-icons';
import BarcodeScanner from '../components/BarcodeScanner';
import BarcodeComponent from '../components/BarcodeComponent';
import LaboratoryLogo from '../components/Laboratory/LaboratoryLogo';
import { formatExpirationDate, formatRelativeDate, formatFullDate } from '../utils/FormatDate';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';
import 'react-datepicker/dist/react-datepicker.css';
import '../components/Form/Fields/InputDate.scss';
import './Inventaire.scss';

registerLocale('fr', fr);

// Renders the calendar popper straight into <body> — the inventory table has
// overflow:hidden (Table.scss), which would otherwise clip an inline popper.
// Same fix used by InputDate/InputDateRange.
const CalendarPortal = ({ children }) => createPortal(children, document.body);

const API = `${API_BASE_URL}/api/v1/inventaire`;
const PRODUCTS_API = `${API_BASE_URL}/api/v1/products`;
const STOCK_API = `${API_BASE_URL}/api/v1/stock`;

const EXPIRY_CRITICAL_DAYS = 30;
const EXPIRY_WARNING_DAYS = 90;

let _tempBatchIdCounter = -1;
const newTempBatchId = () => _tempBatchIdCounter--;

const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
};

// No date → gray placeholder; <30j → red; <90j → orange; else → green (per spec)
const expiryInfo = (dateStr) => {
    if (!dateStr) return { state: 'none', color: '#9ca3af', icon: null, diffDays: null };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exp = new Date(dateStr);
    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (diffDays < EXPIRY_CRITICAL_DAYS) return { state: 'critical', color: '#dc2626', icon: '🔴', diffDays };
    if (diffDays < EXPIRY_WARNING_DAYS) return { state: 'warning', color: '#d97706', icon: '⚠️', diffDays };
    return { state: 'ok', color: '#059669', icon: '✅', diffDays };
};

// Same "MM" then auto-slash then "YY or YYYY" typing convention as the mobile app's
// inventory screen (see mobile/app/inventaire/[id].tsx's formatDateInput/parseDateInput)
// — the year accepts either 2 or 4 digits to save keystrokes, and both are treated as
// "complete" for the purposes of auto-advancing to the next field.
const formatMonthYearInput = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 6);
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}/${d.slice(2)}`;
};

// Stored as the 1st of that month (see the calendar-picker onChange below, which has
// always used this convention) — only whole months are ever asked of the pharmacist.
const parseMonthYearInput = (v) => {
    const p = v.split('/');
    if (p.length !== 2) return null;
    const [mm, yRaw] = p;
    if (mm.length !== 2 || (yRaw.length !== 2 && yRaw.length !== 4)) return null;
    // "20" is the start of a 4-digit year being typed (2028…), not 2020 — completing on it made
    // "05/2028" impossible to type (it stopped at 05/2020). An expiry in 2020 is long past anyway.
    if (yRaw === '20') return null;
    const month = parseInt(mm, 10);
    if (!(month >= 1 && month <= 12)) return null;
    const year = yRaw.length === 2 ? 2000 + parseInt(yRaw, 10) : parseInt(yRaw, 10);
    return `${year}-${mm}-01`;
};

const isoToMonthYearDisplay = (iso) => {
    if (!iso) return '';
    const p = iso.split('-');
    if (p.length !== 3) return '';
    return `${p[1]}/${p[0]}`;
};

// Péremption field — expiration is only ever printed as month/year on packaging, so
// this uses the app's month/year datepicker (day is fixed to the 1st internally)
// instead of the browser's native month input.
//
// Typing is handled by our own formatMonthYearInput/parseMonthYearInput (above) rather
// than react-datepicker's own dateFormat-based parsing, specifically so the year can be
// typed as 2 or 4 digits — customInput's own value/onChange can't be used for this
// (react-datepicker's renderDateInput does React.cloneElement(customInput, {value,
// onChange: this.handleChange, ...}), and cloneElement's config always wins over the
// element's original props, so anything set directly on customInput is silently
// discarded). The supported hook for this is onChangeRaw: it fires before
// react-datepicker's handleChange reads the event, and calling preventDefault() on it
// makes handleChange bail out immediately — combined with the top-level `value` prop
// (distinct from `selected`, and always preferred for display when it's a string), that
// lets us own the displayed text completely while `selected` still drives the calendar.
const ExpiryInput = ({ value, onChange, onComplete, disabled }) => {
    const datePickerRef = useRef(null);
    const wrapperRef = useRef(null);
    const [rawText, setRawText] = useState(() => isoToMonthYearDisplay(value));
    const title = value
        ? `Péremption : ${formatExpirationDate(value)}`
        : 'Date de péremption obligatoire';

    // Keep the displayed text in sync when the value changes from elsewhere (the
    // calendar picker, a bulk edit) rather than from this same input's own typing.
    useEffect(() => {
        setRawText(isoToMonthYearDisplay(value));
    }, [value]);

    // Same outside-click handling as InputDate — the calendar is portalled to
    // <body>, so a plain "click outside wrapperRef" check would also fire (and
    // close the calendar) for clicks on the calendar itself.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                wrapperRef.current && !wrapperRef.current.contains(e.target)
                && !e.target.closest?.('.react-datepicker-popper')
            ) {
                datePickerRef.current?.setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    const handleChangeRaw = (e) => {
        // react-datepicker also calls onChangeRaw when a month is clicked in the calendar —
        // that event has no typed text: let it through to onChange (the calendar path) instead
        // of formatting `undefined` (which threw and swallowed the click)
        if (typeof e?.target?.value !== 'string') return;
        e.preventDefault();
        const formatted = formatMonthYearInput(e.target.value);
        setRawText(formatted);
        const parsed = parseMonthYearInput(formatted);
        if (parsed) {
            onChange({ target: { value: parsed } });
            datePickerRef.current?.setOpen(false);
            onComplete?.();
        }
    };

    return (
        <div className="input-date-wrapper" ref={wrapperRef} style={{ width: 140 }}>
            <DatePicker
                ref={datePickerRef}
                selected={value ? new Date(value) : null}
                value={rawText}
                onChangeRaw={handleChangeRaw}
                onChange={date => {
                    // Only reached via the calendar click — typing always preventDefault()s
                    // in handleChangeRaw above, so react-datepicker's own parsing (which
                    // would otherwise call this) never runs for keystrokes.
                    const iso = date ? `${formatDate(date, 'yyyy-MM')}-01` : '';
                    onChange({ target: { value: iso } });
                    setRawText(isoToMonthYearDisplay(iso));
                }}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                placeholderText="MM/AAAA"
                wrapperClassName="datepicker-wrapper"
                popperContainer={CalendarPortal}
                locale="fr"
                disabled={disabled}
                customInput={
                    <input
                        className="form-control form-control-sm text-center"
                        title={title}
                        required
                    />
                }
            />
            <FontAwesomeIcon
                icon={faCalendarDays}
                className="input-date-icon"
                onClick={() => !disabled && datePickerRef.current?.setOpen(true)}
            />
        </div>
    );
};

const formatAmount = (v) => v != null
    ? new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2 }).format(v) + ' dhs'
    : '—';

const STATUS_META = {
    DRAFT:       { label: 'Brouillon', bg: '#f3f4f6', color: '#6b7280', icon: faFileLines },
    IN_PROGRESS: { label: 'En cours',  bg: '#fef3c7', color: '#d97706', icon: faHourglassHalf },
    VALIDATED:   { label: 'Validé',    bg: '#dcfce7', color: '#16a34a', icon: faCheckCircle },
};

// Same neutral icon chip as the product sheet's price/stock cards
const META_ICON_STYLE = { background: '#f1f5f9', color: '#42464d' };

export default function InventaireBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const { hasRight } = useAuth();
    const canEdit = hasRight('INVENTORY_EDIT');
    const canApplyRight = hasRight('INVENTORY_APPLY');

    const [loading, setLoading] = useState(true);
    const [inventaire, setInventaire] = useState(null);
    const [items, setItems] = useState([]);
    // Table's column Cell renderers are captured once at mount, so they'd
    // otherwise close over a stale `items` — this ref keeps them reading live data.
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const [saving, setSaving] = useState(false);
    const [applying, setApplying] = useState(false);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    const itemRefs = useRef({});

    // Tracks unsaved changes since the last load/save, to warn the pharmacist
    // before they navigate away or apply.
    const [dirty, setDirty] = useState(false);
    const [staleItems, setStaleItems] = useState(() => new Set());

    // After adding a product or pressing Enter in a "Qté réelle" field, this
    // points to the batch whose input should receive focus once rendered.
    const [pendingFocus, setPendingFocus] = useState(null);
    const qtyInputRefs = useRef({});

    // Compact product search bar (replaces the old side-by-side catalog pane) — lets the
    // pharmacist search or scan a medicine and add it straight to the inventory below.
    const [productQuery, setProductQuery] = useState('');
    const [productResults, setProductResults] = useState([]);
    const [productSearchOpen, setProductSearchOpen] = useState(false);
    const [productSearchLoading, setProductSearchLoading] = useState(false);
    const productSearchRef = useRef(null);

    const scanLoadingRef = useRef(false);
    const handleBarcodeScanRef = useRef(null);

    const batchKey = (productId, batchId) => `${productId}::${batchId ?? 'new'}`;

    // Once the targeted batch row is rendered, focus and select its "Qté réelle" input.
    useEffect(() => {
        if (!pendingFocus) return;
        const el = qtyInputRefs.current[batchKey(pendingFocus.productId, pendingFocus.batchId)];
        if (el) {
            el.focus();
            el.select?.();
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setPendingFocus(null);
        }
    }, [pendingFocus, items]);

    // Returns the next batch (wrapping around) whose "Qté réelle" hasn't been
    // entered yet, after the given one — used for Enter-to-advance.
    const findNextUncounted = (fromProductId, fromBatchId) => {
        const flat = items.flatMap(item => item.batches.map(b => ({ productId: item.productId, batchId: b.batchId })));
        const idx = flat.findIndex(b => b.productId === fromProductId && b.batchId === fromBatchId);
        const isUncounted = ({ productId, batchId }) => {
            const item = items.find(i => i.productId === productId);
            const batch = item?.batches.find(b => b.batchId === batchId);
            return batch && (batch.quantiteReelle === '' || batch.quantiteReelle === null);
        };
        for (let i = idx + 1; i < flat.length; i++) if (isUncounted(flat[i])) return flat[i];
        for (let i = 0; i < idx; i++) if (isUncounted(flat[i])) return flat[i];
        return null;
    };

    const handleQtyKeyDown = (e, productId, batchId) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const next = findNextUncounted(productId, batchId);
        if (!next) return;
        setPendingFocus(next);
    };

    // Total batches counted so far vs. total in the inventory, for the
    // "X/Y comptés" progress badge — expiration dates are mandatory before
    // applying, so this also tracks how many lots still lack one.
    const progress = useMemo(() => {
        let total = 0, counted = 0, withExpiry = 0;
        for (const item of items) {
            for (const batch of item.batches) {
                total++;
                if (batch.quantiteReelle !== '' && batch.quantiteReelle !== null) counted++;
                if (batch.expirationDate) withExpiry++;
            }
        }
        return { total, counted, withExpiry };
    }, [items]);

    // One table row per batch — a product with several lots is duplicated into
    // one row per lot (image/name repeated), so adding a lot always shows up as
    // a new visible row instead of a value hidden inside a collapsed group.
    const tableRows = useMemo(() => items.flatMap(item => item.batches.map((batch, idx) => ({
        rowKey: `${item.productId}::${batch.batchId ?? 'none'}`,
        item,
        batch,
        idx,
    }))), [items]);

    // Quick filters (Tous / Non comptés / Comptés) — client-side here since, unlike mobile's paginated API list,
    // the web builder already loads every line up front (see loadInventaire above).
    const [statusFilter, setStatusFilter] = useState('all');

    const isRowCounted = ({ batch }) => batch.quantiteReelle !== '' && batch.quantiteReelle !== null;

    const filterCounts = useMemo(() => ({
        all: tableRows.length,
        uncounted: tableRows.filter(r => !isRowCounted(r)).length,
        counted: tableRows.filter(r => isRowCounted(r)).length,
    }), [tableRows]);

    const filteredTableRows = useMemo(() => {
        switch (statusFilter) {
            case 'uncounted': return tableRows.filter(r => !isRowCounted(r));
            case 'counted':   return tableRows.filter(r => isRowCounted(r));
            default:          return tableRows;
        }
    }, [tableRows, statusFilter]);

    const STATUS_FILTERS = [
        { key: 'all',       label: 'Tous' },
        { key: 'uncounted', label: 'Non comptés' },
        { key: 'counted',   label: 'Comptés' },
    ];

    useEffect(() => {
        setTitle(`Inventaire #${id}`);
        document.title = `Inventaire #${id}`;
        setTitleIcon('ti-clipboard-list');
        loadInventaire();
        return () => { setTitleIcon(null); };
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const es = new EventSource(`${API_BASE_URL}/api/v1/events/stock`, { withCredentials: true });
        es.addEventListener('stock-outbound', (e) => {
            const { orderableId, quantity } = JSON.parse(e.data);
            const affected = itemsRef.current.find(i => i.productId === orderableId);
            if (affected) {
                const alreadyCounted = affected.batches.some(
                    b => b.quantiteReelle !== '' && b.quantiteReelle !== null
                );
                if (alreadyCounted) {
                    setStaleItems(prev => new Set(prev).add(orderableId));
                }
            }
            setItems(prev => prev.map(item => {
                if (item.productId !== orderableId) return item;
                return {
                    ...item,
                    batches: item.batches.map(b => ({
                        ...b,
                        quantity: b.quantity - quantity,
                    })),
                };
            }));
        });
        return () => es.close();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useBreadcrumbSetup([
        { label: 'Inventaire', url: '/inventaire' },
        { label: `#${id}`, url: `/inventaire/${id}` },
    ]);

    // Old out-of-stock sales left behind empty, unidentifiable placeholder batches
    // (no expiration, 0 quantity) that have nothing physical to count, and expired
    // batches shouldn't be counted as inventory at all — hide both from the lots
    // list, falling back to a single synthetic zero-quantity row so there's always
    // at least one row to enter a count against.
    const filterValidBatches = (batches) => {
        const notExpired = batches.filter(b => !isExpired(b.expirationDate));
        const valid = notExpired.filter(b => Number(b.quantity) > 0 || b.expirationDate);
        if (valid.length > 0) return valid;
        if (notExpired.length > 0) return notExpired.slice(0, 1);
        return [{
            batchId: null, batchNumber: null, quantity: 0,
            expirationDate: '', originalExpirationDate: '',
            quantiteReelle: '', persisted: false,
        }];
    };

    // Groups the flat per-batch lignes returned by the API into one entry per
    // product, each carrying its own batches (one InventaireLigne per OrderableBatch).
    const groupLignesByProduct = (lignes) => {
        const byProduct = new Map();
        for (const l of lignes) {
            if (!byProduct.has(l.orderableId)) {
                byProduct.set(l.orderableId, {
                    productId: l.orderableId,
                    name: l.orderableName,
                    barcode: l.barcode,
                    presentation: l.presentation,
                    dosage: l.dosage,
                    laboratoryId: l.laboratoryId,
                    laboratoryName: l.laboratoryName,
                    image: null,
                    batches: [],
                });
            }
            byProduct.get(l.orderableId).batches.push({
                batchId: l.batchId,
                batchNumber: l.batchNumber,
                quantity: l.currentStock ?? (l.quantiteTheorique - (l.ventesEnCours ?? 0)),
                expirationDate: l.expirationDate || '',
                originalExpirationDate: l.expirationDate || '',
                quantiteReelle: l.quantiteReelle ?? '',
                persisted: true,
            });
        }
        return Array.from(byProduct.values()).map(item => ({ ...item, batches: filterValidBatches(item.batches) }));
    };

    const loadInventaire = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/${id}`, { withCredentials: true });
            setInventaire(data);
            const pageTitle = data.title ? data.title : `Inventaire #${data.id}`;
            setTitle(pageTitle);
            setItems(groupLignesByProduct(data.lignes || []));
            setDirty(false);
        } catch {
            toast.error("Inventaire introuvable");
            navigate('/inventaire');
        } finally {
            setLoading(false);
        }
    };

    // ── Right pane: build the inventory locally (nothing persisted yet) ──
    // Adding a product fetches its real OrderableBatches and shows one sub-row
    // per batch; products with no batch get a single synthetic "no batch" row.
    const addItem = async (product) => {
        if (!canEdit) return;
        const existing = itemsRef.current.find(i => i.productId === product.id);
        if (existing) {
            const el = itemRefs.current[product.id];
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('inv-row-product--flash');
                setTimeout(() => el.classList.remove('inv-row-product--flash'), 800);
            }
            setPendingFocus({ productId: product.id, batchId: existing.batches[0].batchId });
            return;
        }

        let batches;
        try {
            const { data } = await axios.get(`${STOCK_API}/orderable/${product.id}/batches`, {
                params: { page: 0, size: 100 },
                withCredentials: true,
            });
            batches = filterValidBatches((data.content || []).map(b => ({
                batchId: b.id,
                batchNumber: b.batchNumber,
                quantity: b.quantity,
                expirationDate: b.expirationDate || '',
                originalExpirationDate: b.expirationDate || '',
                quantiteReelle: '',
                persisted: false,
            })));
        } catch {
            toast.error('Impossible de charger les lots de ce produit');
            return;
        }

        if (batches.length === 0) {
            batches = [{
                batchId: null, batchNumber: null, quantity: product.totalStock ?? 0,
                expirationDate: '', originalExpirationDate: '',
                quantiteReelle: '', persisted: false,
            }];
        }

        setItems(prev => prev.some(i => i.productId === product.id) ? prev : [...prev, {
            productId: product.id,
            name: product.name,
            barcode: product.barcode,
            presentation: product.presentation,
            dosage: product.dosage,
            laboratoryId: product.laboratory?.id,
            laboratoryName: product.laboratory?.name,
            image: product.images?.length > 0 ? product.images[0].url : null,
            batches,
        }]);
        setPendingFocus({ productId: product.id, batchId: batches[0].batchId });
        setDirty(true);
    };

    // Table's column Cell renderers are also captured once at mount — this ref
    // lets them always invoke the current `addItem` (live `items`/`setItems` closure).
    const addItemRef = useRef(addItem);
    addItemRef.current = addItem;

    // Barcode scan handler — triggered by the global BarcodeScanner hook when
    // a rapid keystroke sequence (hardware scanner) is detected anywhere on the page.
    const handleBarcodeScan = async (barcode) => {
        if (scanLoadingRef.current) return;
        const query = barcode.trim();
        if (!query) return;

        scanLoadingRef.current = true;
        try {
            const { data } = await axios.get(PRODUCTS_API, {
                params: { keyword: query, page: 0, size: 5 },
                withCredentials: true,
            });
            const results = data.content || [];
            const match = results.find(d => d.barcode === query) || (results.length === 1 ? results[0] : null);
            if (!match) {
                toast.warning(results.length > 1 ? 'Plusieurs résultats, soyez plus précis' : 'Aucun médicament trouvé');
                return;
            }
            await addItemRef.current(match);
            setProductQuery('');
            setProductResults([]);
            setProductSearchOpen(false);
        } catch {
            toast.error('Erreur lors de la recherche');
        } finally {
            scanLoadingRef.current = false;
        }
    };
    handleBarcodeScanRef.current = handleBarcodeScan;

    BarcodeScanner({
        onComplete: (code) => handleBarcodeScanRef.current(code),
        onError: (code) => console.warn('scan error:', code),
        minLength: 5,
        // Unlike most pages, scanning here IS the workflow: after each match the matched
        // row's "Qté réelle" field is auto-focused (see the pendingFocus effect above) so
        // the next scan can fire right away — blocking scans while an input is focused
        // would break that same chain it depends on.
        ignoreEditableFocus: false,
    });

    // Lets a barcode be pasted (e.g. from a spreadsheet) instead of physically scanned —
    // same lookup-and-add path as a real scan.
    useEffect(() => {
        const onPaste = (event) => {
            const text = event.clipboardData?.getData('text')?.trim();
            if (text) handleBarcodeScanRef.current(text);
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, []);

    // Debounced search feeding the compact product search bar's dropdown.
    const fetchProductResults = useCallback(async (keyword) => {
        if (!keyword.trim()) {
            setProductResults([]);
            setProductSearchLoading(false);
            return;
        }
        setProductSearchLoading(true);
        try {
            const { data } = await axios.get(PRODUCTS_API, {
                params: { keyword, page: 0, size: 8, prefixMatch: true },
                withCredentials: true,
            });
            setProductResults(data.content || []);
        } catch {
            setProductResults([]);
        } finally {
            setProductSearchLoading(false);
        }
    }, []);

    const debouncedProductSearch = useMemo(() => debounce(fetchProductResults, 300), [fetchProductResults]);
    useEffect(() => () => debouncedProductSearch.cancel(), [debouncedProductSearch]);

    const handleProductQueryChange = (value) => {
        setProductQuery(value);
        setProductSearchOpen(true);
        if (!value.trim()) {
            setProductResults([]);
            debouncedProductSearch.cancel();
        } else {
            debouncedProductSearch(value);
        }
    };

    const handleSelectProductResult = (product) => {
        addItemRef.current(product);
        setProductQuery('');
        setProductResults([]);
        setProductSearchOpen(false);
    };

    // Close the search dropdown on an outside click.
    useEffect(() => {
        if (!productSearchOpen) return;
        const handleClickOutside = (e) => {
            if (productSearchRef.current && !productSearchRef.current.contains(e.target)) {
                setProductSearchOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [productSearchOpen]);

    const updateBatchQuantity = (productId, batchId, value) => {
        if (value !== '' && !/^\d*$/.test(value)) return;
        setStaleItems(prev => { const n = new Set(prev); n.delete(productId); return n; });
        setItems(prev => prev.map(item => item.productId !== productId ? item : {
            ...item,
            batches: item.batches.map(b => b.batchId === batchId ? { ...b, quantiteReelle: value } : b),
        }));
        setDirty(true);
    };

    const updateBatchExpirationDate = (productId, batchId, value) => {
        setItems(prev => prev.map(item => item.productId !== productId ? item : {
            ...item,
            batches: item.batches.map(b => b.batchId === batchId ? { ...b, expirationDate: value } : b),
        }));
        setDirty(true);
    };

    const addBatchToItem = (productId) => {
        const tempId = newTempBatchId();
        setItems(prev => prev.map(item => item.productId !== productId ? item : {
            ...item,
            batches: [...item.batches, {
                batchId: tempId,
                batchNumber: null,
                quantity: 0,
                expirationDate: '',
                originalExpirationDate: '',
                quantiteReelle: '',
                persisted: false,
            }],
        }));
        setPendingFocus({ productId, batchId: tempId });
        setDirty(true);
    };

    // Removes a single lot. A multi-lot product otherwise has no way to shed just
    // one row without deleting the whole product (see removeItem), so an already-saved
    // batch needs its own backend call rather than only being dropped from local state.
    const removeBatchFromItem = async (productId, batchId) => {
        const item = items.find(i => i.productId === productId);
        const batch = item?.batches.find(b => b.batchId === batchId);
        setItems(prev => prev.map(i => i.productId !== productId ? i : {
            ...i,
            batches: i.batches.filter(b => b.batchId !== batchId),
        }));
        setDirty(true);

        if (batch?.persisted) {
            try {
                await axios.delete(`${API}/${id}/product/${productId}/batch/${batchId}`, { withCredentials: true });
            } catch (err) {
                toast.error(err.response?.data?.message || "Erreur lors de la suppression du lot");
            }
        }
    };

    const removeItem = async (productId) => {
        const item = items.find(i => i.productId === productId);
        setItems(prev => prev.filter(i => i.productId !== productId));
        setDirty(true);

        if (item?.batches.some(b => b.persisted)) {
            try {
                await axios.delete(`${API}/${id}/product/${productId}`, { withCredentials: true });
            } catch (err) {
                toast.error(err.response?.data?.message || "Erreur lors de la suppression");
            }
        }
    };

    // Downloads the selected rows as a CSV file — there's no backend export
    // endpoint for an in-progress inventaire draft, so this is built client-side.
    const exportRowsAsCsv = (rows) => {
        if (rows.length === 0) return;
        const header = ['ID', 'Médicament', 'Code barre', 'Lot', 'Péremption', 'Stock', 'Qté réelle', 'Écart'];
        const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = [header.map(escape).join(';')];
        for (const { item, batch } of rows) {
            const qReelle = batch.quantiteReelle === '' ? null : Number(batch.quantiteReelle);
            const ecart = qReelle !== null ? qReelle - batch.quantity : '';
            lines.push([
                item.productId, item.name, item.barcode || '', batch.batchNumber || '',
                batch.expirationDate || '', batch.quantity, batch.quantiteReelle, ecart,
            ].map(escape).join(';'));
        }
        const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventaire-${id}-selection.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Bulk actions for the Table's multiSelect banner — each receives the
    // selected rowKeys, resolved back to their {item, batch} pair.
    const inventorySelectionActions = [
        ...(canEdit ? [
            {
                label: 'Supprimer les lots',
                icon: faTrash,
                variant: 'danger',
                onClick: (rowKeys) => {
                    for (const rowKey of rowKeys) {
                        const row = tableRows.find(r => r.rowKey === rowKey);
                        if (!row) continue;
                        if (row.item.batches.length > 1) {
                            removeBatchFromItem(row.item.productId, row.batch.batchId);
                        } else {
                            removeItem(row.item.productId);
                        }
                    }
                },
            },
            {
                label: 'Marquer compté (= stock)',
                icon: faCheckCircle,
                onClick: (rowKeys) => {
                    for (const rowKey of rowKeys) {
                        const row = tableRows.find(r => r.rowKey === rowKey);
                        if (!row || row.batch.quantity < 0) continue;
                        updateBatchQuantity(row.item.productId, row.batch.batchId, String(row.batch.quantity));
                    }
                },
            },
        ] : []),
        {
            label: 'Exporter la sélection',
            icon: faDownload,
            onClick: (rowKeys) => {
                exportRowsAsCsv(tableRows.filter(r => rowKeys.includes(r.rowKey)));
            },
        },
    ];

    // Upserts one InventaireLigne per OrderableBatch sub-row (PUT/POST
    // .../product/{productId}/batch[/{batchId}]) and returns the resulting
    // `items` with each batch's persisted state refreshed. Shared by the
    // explicit "Enregistrer" action and by "Appliquer", which must persist
    // the draft first — the backend's summary/validate endpoints only see
    // what's already saved, not the in-memory local state.
    const persistItems = async () => {
        const updated = [];
        for (const item of items) {
            const updatedBatches = [];
            for (const batch of item.batches) {
                if (batch.persisted && (batch.quantiteReelle === '' || batch.quantiteReelle === null)) {
                    // Already exists in the inventaire and still uncounted — nothing to persist.
                    updatedBatches.push(batch);
                    continue;
                }
                const quantiteReelle = (batch.quantiteReelle === '' || batch.quantiteReelle === null)
                    ? null
                    : Number(batch.quantiteReelle);
                if (batch.batchId != null && batch.batchId > 0) {
                    const { data } = await axios.put(
                        `${API}/${id}/product/${item.productId}/batch/${batch.batchId}`,
                        { quantiteReelle },
                        { withCredentials: true }
                    );
                    updatedBatches.push({ ...batch, quantity: data.currentStock ?? data.quantiteTheorique, persisted: true });
                } else {
                    const { data } = await axios.post(
                        `${API}/${id}/product/${item.productId}/batch`,
                        { quantiteReelle, batchNumber: batch.batchNumber || null },
                        { withCredentials: true }
                    );
                    updatedBatches.push({
                        ...batch,
                        batchId: data.batchId,
                        batchNumber: data.batchNumber,
                        quantity: data.currentStock ?? data.quantiteTheorique,
                        persisted: true,
                    });
                }
            }
            updated.push({ ...item, batches: updatedBatches });
        }
        return updated;
    };

    // ── Enregistrer: persist the draft selection without touching the stock ──
    const handleSave = async () => {
        const allBatches = items.flatMap(item => item.batches);
        if (allBatches.length === 0) {
            toast.warning('Ajoutez au moins un médicament avant d\'enregistrer');
            return;
        }

        setSaving(true);
        try {
            setItems(await persistItems());
            setDirty(false);
            toast.success('Inventaire enregistré');
        } catch (err) {
            toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    // ── Appliquer: define the real stock from the saved draft (écart-based adjustment) ──
    const openApplyModal = async () => {
        if (progress.total === 0 || progress.counted < progress.total) {
            toast.warning('Renseignez une quantité réelle pour chaque lot avant d\'appliquer');
            return;
        }
        if (progress.withExpiry < progress.total) {
            toast.warning('Renseignez une date de péremption pour chaque lot avant d\'appliquer');
            return;
        }

        setLoadingSummary(true);
        try {
            if (dirty) {
                setItems(await persistItems());
                setDirty(false);
            }
            const { data } = await axios.get(`${API}/${id}/summary`, { withCredentials: true });
            setSummary(data);
            setShowApplyModal(true);
        } catch (err) {
            toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement du comptage");
        } finally {
            setLoadingSummary(false);
        }
    };

    const handleApply = async () => {
        setApplying(true);
        try {
            // Push expiration-date edits first (only when changed — spec §2),
            // each via the new PATCH .../stock/batches/{batchId}/expiration
            for (const item of items) {
                for (const batch of item.batches) {
                    if (batch.batchId != null && batch.expirationDate !== batch.originalExpirationDate) {
                        await axios.patch(
                            `${STOCK_API}/batches/${batch.batchId}/expiration`,
                            { expirationDate: batch.expirationDate || null },
                            { withCredentials: true }
                        );
                    }
                }
            }

            await axios.post(`${API}/${id}/validate`, {}, { withCredentials: true });
            toast.success('Stock mis à jour avec succès');
            setShowApplyModal(false);
            navigate('/inventaire');
        } catch (err) {
            toast.error(err.response?.data?.message || "Erreur lors de l'application de l'inventaire");
        } finally {
            setApplying(false);
        }
    };

    const canApply = items.some(i => i.batches.some(b => b.persisted));

    // Client-side stats for the apply summary modal — the backend summary has
    // no notion of in-progress, unsaved expiration-date edits (spec §4)
    const expirationStats = useMemo(() => {
        const updated = [];
        const expiringSoon = [];
        for (const item of items) {
            for (const batch of item.batches) {
                if (batch.batchId != null && batch.expirationDate && batch.expirationDate !== batch.originalExpirationDate) {
                    updated.push({ productName: item.name, batchNumber: batch.batchNumber });
                }
                if (batch.expirationDate) {
                    const info = expiryInfo(batch.expirationDate);
                    if (info.state === 'critical') {
                        expiringSoon.push({ productName: item.name, batchNumber: batch.batchNumber, diffDays: info.diffDays });
                    }
                }
            }
        }
        return { updated, expiringSoon };
    }, [items]);

    // Flattens the grouped product→batches items into one row per batch, for
    // the read-only recap (each InventaireLigne is its own batch in this model)
    const validatedRows = useMemo(() => items.flatMap(item => item.batches.map(b => ({
        rowKey: b.batchId ?? `${item.productId}-new`,
        productId: item.productId,
        name: item.name,
        barcode: item.barcode,
        presentation: item.presentation,
        dosage: item.dosage,
        laboratoryId: item.laboratoryId,
        laboratoryName: item.laboratoryName,
        image: item.image,
        batchNumber: b.batchNumber,
        quantiteTheorique: b.quantity,
        quantiteReelle: b.quantiteReelle,
    }))), [items]);

    if (loading) {
        return (
            <div className="page-content text-center py-5">
                <span className="spinner-border text-primary" />
            </div>
        );
    }

    if (!inventaire) return null;

    // ── Read-only recap once the inventaire has been validated/locked ──
    if (inventaire.status === 'VALIDATED') {
        return (
            <div className="page-content page-content--inventaire inv-builder">
                <div className="inv-meta-panel">
                    <div className="inv-meta-panel__item">
                        <span className="inv-meta-panel__icon" style={META_ICON_STYLE}>
                            <FontAwesomeIcon icon={inventaire.mode === 'TOURNANT' ? faArrowsRotate : faHandPointer} />
                        </span>
                        <div>
                            <span className="inv-meta-panel__label">Mode</span>
                            <span className="inv-meta-panel__value">
                                {inventaire.mode === 'TOURNANT' ? 'Tournant' : 'Manuel'}
                            </span>
                        </div>
                    </div>
                    <div className="inv-meta-panel__item">
                        {(() => {
                            const s = STATUS_META[inventaire.status] || STATUS_META.DRAFT;
                            return (
                                <>
                                    <span className="inv-meta-panel__icon" style={META_ICON_STYLE}>
                                        <FontAwesomeIcon icon={s.icon} />
                                    </span>
                                    <div>
                                        <span className="inv-meta-panel__label">Statut</span>
                                        <span className="inv-meta-panel__value" style={{ color: s.color }}>{s.label}</span>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                    <div className="inv-meta-panel__item">
                        <span className="inv-meta-panel__icon" style={META_ICON_STYLE}>
                            <FontAwesomeIcon icon={faCalendarDays} />
                        </span>
                        <div>
                            <span className="inv-meta-panel__label">Date de création</span>
                            <span
                                className="inv-meta-panel__value"
                                data-tooltip-id="tooltip"
                                data-tooltip-content={formatFullDate(inventaire.date)}
                            >
                                {formatRelativeDate(inventaire.date)}
                            </span>
                        </div>
                    </div>
                    <div className="inv-meta-panel__item">
                        <span className="inv-meta-panel__icon" style={META_ICON_STYLE}>
                            <FontAwesomeIcon icon={faUser} />
                        </span>
                        <div>
                            <span className="inv-meta-panel__label">Créé par</span>
                            <span
                                className="inv-meta-panel__value"
                                data-tooltip-id="tooltip"
                                data-tooltip-content={inventaire.createdByName || undefined}
                            >
                                {inventaire.createdByName || '—'}
                            </span>
                        </div>
                    </div>
                    <div className="inv-meta-panel__item">
                        <span className="inv-meta-panel__icon" style={META_ICON_STYLE}>
                            <FontAwesomeIcon icon={faCheckCircle} />
                        </span>
                        <div>
                            <span className="inv-meta-panel__label">Validé le</span>
                            <span
                                className="inv-meta-panel__value"
                                data-tooltip-id="tooltip"
                                data-tooltip-content={formatFullDate(inventaire.validatedAt)}
                            >
                                {formatRelativeDate(inventaire.validatedAt)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="inv-pane">
                    <div className="inv-pane__title">
                        <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 11, color: '#9ca3af' }} />
                        Lignes comptées
                        {validatedRows.length > 0 && <span className="inv-count-badge">{validatedRows.length}</span>}
                    </div>
                    <div className="inv-pane__table">
                    <Table
                        mode="client"
                        rowId="rowKey"
                        data={validatedRows}
                        columns={[
                            {
                                Header: 'Médicament',
                                accessor: 'name',
                                minWidth: '320px',
                                Cell: (row) => (
                                    <div className="d-flex align-items-center gap-3">
                                        <img
                                            src={row.image || '/images/no_product.png'}
                                            alt={row.name}
                                            style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                                        />
                                        <div>
                                            <span
                                                className="cell-name"
                                                style={{ cursor: 'pointer' }}
                                                onClick={(e) => { e.stopPropagation(); navigate(`/products/${row.productId}`); }}
                                            >
                                                {row.name}
                                            </span>
                                            {(row.presentation || row.dosage) && (
                                                <span className="cell-sub" style={{ color: '#111827' }}>
                                                    {[row.presentation, row.dosage].filter(Boolean).join(' · ')}
                                                </span>
                                            )}
                                            {row.batchNumber && (
                                                <span className="cell-sub">
                                                    Lot {row.batchNumber}
                                                </span>
                                            )}
                                            {row.laboratoryName && (
                                                <span className="cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                                                    Par <LaboratoryLogo id={row.laboratoryId} name={row.laboratoryName} size={16} />
                                                    {row.laboratoryName}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                Header: 'Code-barres',
                                accessor: 'barcode',
                                className: 'text-center',
                                Cell: (row) => row.barcode
                                    ? (
                                        <div style={{ width: 140, margin: '0 auto' }}>
                                            <BarcodeComponent format="EAN13" value={row.barcode} height={22} fontSize={11} />
                                        </div>
                                    )
                                    : <span className="cell-muted fst-italic">Aucun</span>,
                            },
                            {
                                Header: 'Stock théorique',
                                accessor: 'quantiteTheorique',
                                className: 'text-center',
                                Cell: (row) => (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center',
                                        background: '#f3f4f6', color: '#374151',
                                        padding: '4px 10px', borderRadius: 999,
                                        fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                                    }}>
                                        {row.quantiteTheorique}
                                    </span>
                                ),
                            },
                            {
                                Header: 'Compté',
                                accessor: 'quantiteReelle',
                                className: 'text-center',
                                Cell: (row) => {
                                    const qReelle = row.quantiteReelle === '' || row.quantiteReelle === null ? null : Number(row.quantiteReelle);
                                    return qReelle !== null
                                        ? (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center',
                                                background: '#eff6ff', color: '#1d4ed8',
                                                padding: '4px 10px', borderRadius: 999,
                                                fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                                            }}>
                                                {qReelle}
                                            </span>
                                        )
                                        : <span className="cell-muted">—</span>;
                                },
                            },
                            {
                                Header: 'Écart',
                                accessor: '_ecart',
                                className: 'text-center',
                                Cell: (row) => {
                                    const qReelle = row.quantiteReelle === '' || row.quantiteReelle === null ? null : Number(row.quantiteReelle);
                                    const ecart = qReelle !== null ? qReelle - row.quantiteTheorique : null;
                                    if (ecart === null || ecart === 0) return <span className="cell-muted">—</span>;
                                    const positive = ecart > 0;
                                    return (
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 6,
                                            background: positive ? '#ecfdf5' : '#fef2f2',
                                            color: positive ? '#065f46' : '#991b1b',
                                            padding: '4px 10px', borderRadius: 999,
                                            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                                        }}>
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: positive ? '#10b981' : '#ef4444', flexShrink: 0 }} />
                                            {positive ? `+${ecart}` : ecart}
                                        </span>
                                    );
                                },
                            },
                        ]}
                    />
                    </div>
                </div>
            </div>
        );
    }

    // ── Editable single-pane builder for draft / in-progress inventaires ──
    return (
        <div className="page-content page-content--inventaire inv-builder">
            <div className="inv-builder__panes">
                <div className="inv-pane">
                    {/* Table's own search bar doubles as the "add a product" search — typing
                        both filters the list below and opens a catalog dropdown to add a match. */}
                    <div className="inv-pane__table" ref={productSearchRef}>
                        <Table
                            mode="client"
                            rowId="rowKey"
                            data={filteredTableRows}
                            pageSize={1000}
                            searchable
                            keyword={productQuery}
                            setKeyword={handleProductQueryChange}
                            searchPlaceholder="Rechercher ou scanner un médicament..."
                            hoverable={false}
                            // a counted line (real quantity entered) gets the whole row in green
                            rowClassName={(row) => (isRowCounted(row) ? 'row-matched' : '')}
                            multiSelect
                            selectionActions={inventorySelectionActions}
                            showViewsButton={false}
                            toolbarExtra={
                                <>
                                    <div className="inv-status-filters">
                                        {STATUS_FILTERS.map(f => (
                                            <button
                                                key={f.key}
                                                type="button"
                                                className={`inv-status-filter${statusFilter === f.key ? ' inv-status-filter--active' : ''}`}
                                                onClick={() => setStatusFilter(f.key)}
                                            >
                                                {f.label}
                                                <span className="inv-status-filter__count">{filterCounts[f.key]}</span>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            }
                            emptyMessage={
                                <div className="inv-empty inv-empty--lg">
                                    <FontAwesomeIcon icon={faBoxesStacked} />
                                    <p>Recherchez ou scannez un médicament ci-dessus pour l'ajouter à votre inventaire</p>
                                </div>
                            }
                            columns={[
                                {
                                    Header: '#',
                                    // Not '_id': renaming the accessor changes the Table's column bookmark
                                    // key, so previously saved layouts don't override the hidden default.
                                    accessor: '_productId',
                                    className: 'text-center',
                                    width: '60px',
                                    visible: false,
                                    Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.item.productId}</span>,
                                },
                                {
                                    Header: 'Médicament',
                                    accessor: 'name',
                                    minWidth: '260px',
                                    Cell: (row) => {
                                        const { item, batch } = row;
                                        const counted = batch.quantiteReelle !== '' && batch.quantiteReelle !== null;
                                        const grouped = item.batches.length > 1;
                                        return (
                                            <div
                                                ref={el => { if (el) itemRefs.current[item.productId] = el; }}
                                                className={`inv-row-product ${counted ? 'inv-row-product--counted' : ''} ${grouped ? 'inv-row-product--grouped' : ''}`}
                                            >
                                                <img src={item.image || '/images/no_product.png'} alt={item.name} className="inv-row-product__img" />
                                                <div className="inv-item-group__info">
                                                    <div className="inv-item-group__name">{item.name}</div>
                                                    {(item.presentation || item.dosage) && (
                                                        <div className="inv-item-group__sub" style={{ color: '#111827' }}>
                                                            {[item.presentation, item.dosage].filter(Boolean).join(' · ')}
                                                        </div>
                                                    )}
                                                    {item.laboratoryName && (
                                                        <div className="inv-item-group__sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <LaboratoryLogo id={item.laboratoryId} name={item.laboratoryName} size={14} />
                                                            {item.laboratoryName}
                                                        </div>
                                                    )}
                                                </div>
                                                {staleItems.has(item.productId) && (
                                                    <span className="inv-stale-badge" title="Le stock a changé après votre comptage — vérifiez ou recomptez">
                                                        <FontAwesomeIcon icon={faTriangleExclamation} />
                                                        Stock modifié
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    },
                                },
                                {
                                    Header: 'Code-barres',
                                    accessor: '_barcode',
                                    className: 'text-center',
                                    Cell: (row) => row.item.barcode && row.item.barcode !== 'N/A'
                                        ? (
                                            <div style={{ width: 140, margin: '0 auto' }}>
                                                <BarcodeComponent format="EAN13" value={row.item.barcode} height={22} fontSize={11} />
                                            </div>
                                        )
                                        : <span className="cell-muted fst-italic">Aucun</span>,
                                },
                                {
                                    Header: 'Stock',
                                    accessor: '_stock',
                                    className: 'text-center',
                                    minWidth: '80px',
                                    Cell: (row) => <span className="inv-stock-badge">{row.batch.quantity}</span>,
                                },
                                {
                                    Header: 'Péremption',
                                    accessor: '_expiry',
                                    minWidth: '140px',
                                    maxWidth: '160px',
                                    Cell: (row) => {
                                        const { item, batch } = row;
                                        return (
                                            <ExpiryInput
                                                value={batch.expirationDate}
                                                onChange={e => updateBatchExpirationDate(item.productId, batch.batchId, e.target.value)}
                                                onComplete={() => {
                                                    const el = qtyInputRefs.current[batchKey(item.productId, batch.batchId)];
                                                    el?.focus();
                                                    el?.select?.();
                                                }}
                                                disabled={!canEdit}
                                            />
                                        );
                                    },
                                },
                                {
                                    Header: 'Qté réelle',
                                    accessor: '_qty',
                                    minWidth: '90px',
                                    maxWidth: '110px',
                                    Cell: (row) => {
                                        const { item, batch } = row;
                                        return (
                                            <input
                                                ref={el => { qtyInputRefs.current[batchKey(item.productId, batch.batchId)] = el; }}
                                                type="text"
                                                inputMode="numeric"
                                                className="form-control form-control-sm text-center"
                                                style={{ width: 90 }}
                                                placeholder="—"
                                                value={batch.quantiteReelle}
                                                disabled={!canEdit}
                                                onChange={e => updateBatchQuantity(item.productId, batch.batchId, e.target.value)}
                                                onKeyDown={e => handleQtyKeyDown(e, item.productId, batch.batchId)}
                                            />
                                        );
                                    },
                                },
                                {
                                    Header: '',
                                    accessor: '_actions',
                                    className: 'col-actions',
                                    Cell: (row) => {
                                        const { item, batch } = row;
                                        const multi = item.batches.length > 1;
                                        if (!canEdit) return null;
                                        return (
                                            <div className="cell-dropdown">
                                                <button
                                                    type="button"
                                                    className="cell-actions-btn dropdown-toggle"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                >
                                                    <FontAwesomeIcon icon={faEllipsisH} />
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-end">
                                                    <li>
                                                        <button className="dropdown-item" onClick={() => addBatchToItem(item.productId)}>
                                                            <FontAwesomeIcon icon={faPlus} className="me-2 text-muted" />
                                                            Ajouter un lot
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => multi
                                                                ? removeBatchFromItem(item.productId, batch.batchId)
                                                                : removeItem(item.productId)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} className="me-2 text-muted" />
                                                            {multi ? 'Supprimer ce lot' : 'Retirer'}
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                        );
                                    },
                                },
                            ]}
                        />

                        {productSearchOpen && productQuery && (
                            <div className="inv-product-search__dropdown inv-product-search__dropdown--anchored">
                                {productSearchLoading && (
                                    <div className="inv-product-search__loading">
                                        <span className="spinner-border spinner-border-sm" />
                                    </div>
                                )}
                                {!productSearchLoading && productResults.length === 0 && (
                                    <div className="inv-product-search__empty">Aucun médicament trouvé</div>
                                )}
                                {!productSearchLoading && productResults.map(product => {
                                    const qty = product.totalStock ?? 0;
                                    const [color, bg] = qty > 3
                                        ? ['#065f46', '#d1fae5']
                                        : qty > 0
                                        ? ['#92400e', '#fef3c7']
                                        : qty < 0
                                        ? ['#991b1b', '#fee2e2']
                                        : ['#6b7280', '#f3f4f6'];
                                    return (
                                        <button
                                            type="button"
                                            key={product.id}
                                            className="inv-product-search__item"
                                            disabled={!canEdit}
                                            onClick={() => handleSelectProductResult(product)}
                                        >
                                            <img
                                                src={product.images?.length > 0 ? product.images[0].url : '/images/no_product.png'}
                                                alt={product.name}
                                            />
                                            <div className="inv-product-search__item-info">
                                                <span className="inv-product-search__item-name">{product.name}</span>
                                                {(product.presentation || product.dosage) && (
                                                    <span className="inv-product-search__item-sub">
                                                        {[product.presentation, product.dosage].filter(Boolean).join(' · ')}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="inv-stock-badge" style={{ color, background: bg }}>{qty}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="inv-pane__footer">
                        <span className="inv-pane__hint">
                            {canApply
                                ? 'Vérifiez le résumé avant d\'appliquer les changements au stock'
                                : 'Enregistrez votre sélection pour ne pas la perdre, avant de l\'appliquer au stock'}
                        </span>
                        <div className="d-flex gap-2 align-items-center">
                            {dirty && <span className="inv-dirty-badge">Non enregistré</span>}
                            {canEdit && (
                                <button
                                    type="button"
                                    className="inv-btn inv-btn--save"
                                    onClick={handleSave}
                                    disabled={saving || items.length === 0}
                                >
                                    {saving
                                        ? <><span className="spinner-border spinner-border-sm" />Enregistrement...</>
                                        : <><FontAwesomeIcon icon={faSave} />Enregistrer</>
                                    }
                                </button>
                            )}
                            {canApplyRight && (
                                <button
                                    type="button"
                                    className="inv-btn inv-btn--apply"
                                    onClick={openApplyModal}
                                    disabled={!canApply || loadingSummary}
                                    title={!canApply ? 'Enregistrez d\'abord votre inventaire' : ''}
                                >
                                    {loadingSummary
                                        ? <><span className="spinner-border spinner-border-sm" />Chargement...</>
                                        : <><FontAwesomeIcon icon={faCheckCircle} />Appliquer</>
                                    }
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Apply confirmation modal */}
            {summary && (
                <Modal
                    show={showApplyModal}
                    onClose={() => setShowApplyModal(false)}
                    title="Appliquer l'inventaire au stock"
                    classSize="modal-dialog-centered"
                    icon={faCheckCircle}
                    iconBg="#dcfce7"
                    iconColor="#16a34a"
                    footer={
                        <>
                            <button className="btn btn-light" onClick={() => setShowApplyModal(false)}>
                                Annuler
                            </button>
                            <button
                                className="btn btn-success fw-bold"
                                onClick={handleApply}
                                disabled={applying}
                            >
                                {applying
                                    ? <><span className="spinner-border spinner-border-sm me-2" />Application...</>
                                    : <><FontAwesomeIcon icon={faCheckCircle} className="me-2" />Confirmer et appliquer</>
                                }
                            </button>
                        </>
                    }
                >
                    <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
                        Les ajustements suivants seront appliqués au stock de façon irréversible.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div style={{ background: '#f9fafb', borderRadius: 10, padding: '14px 16px' }}>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>Lignes comptées</div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>
                                {summary.lignesComptees}{' '}
                                <span style={{ fontSize: 13, color: '#6b7280' }}>/ {summary.totalLignes}</span>
                            </div>
                        </div>
                        <div style={{ background: '#f9fafb', borderRadius: 10, padding: '14px 16px' }}>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>Valeur des écarts</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>
                                {formatAmount(summary.valeurTotaleEcarts)}
                            </div>
                        </div>
                        <div style={{ background: '#dcfce7', borderRadius: 10, padding: '14px 16px' }}>
                            <div style={{ fontSize: 12, color: '#166534' }}>Ajustements positifs</div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}>
                                +{summary.ajustementsPositifs}
                            </div>
                        </div>
                        <div style={{ background: '#fef2f2', borderRadius: 10, padding: '14px 16px' }}>
                            <div style={{ fontSize: 12, color: '#991b1b' }}>Ajustements négatifs</div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>
                                -{summary.ajustementsNegatifs}
                            </div>
                        </div>
                        <div style={{ background: '#f9fafb', borderRadius: 10, padding: '14px 16px', gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>Dates de péremption mises à jour</div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>
                                {expirationStats.updated.length}
                            </div>
                        </div>
                    </div>

                    {expirationStats.expiringSoon.length > 0 && (
                        <div style={{
                            background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
                            padding: '10px 14px', fontSize: 13, color: '#92400e', marginTop: 10,
                        }}>
                            <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                            {expirationStats.expiringSoon.length === 1 ? (
                                <>
                                    Le lot <strong>{expirationStats.expiringSoon[0].batchNumber}</strong> de{' '}
                                    <strong>{expirationStats.expiringSoon[0].productName}</strong> expire dans{' '}
                                    {expirationStats.expiringSoon[0].diffDays} jour{expirationStats.expiringSoon[0].diffDays > 1 ? 's' : ''}.
                                </>
                            ) : (
                                <>
                                    {expirationStats.expiringSoon.length} lots expirent dans moins de {EXPIRY_CRITICAL_DAYS} jours :{' '}
                                    {expirationStats.expiringSoon.map((b, i) => (
                                        <span key={i}>
                                            {i > 0 && ', '}
                                            <strong>{b.batchNumber}</strong> ({b.productName})
                                        </span>
                                    ))}
                                </>
                            )}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}
